// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useMemo, useState, useEffect } from 'react';
import { Image, Pressable, StyleSheet, Platform } from 'react-native';
import { Redirect, useRootNavigationState, useLocalSearchParams } from 'expo-router';

import { Text, View } from '@/components/Themed';
import { useUser } from './user-context';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useI18n } from '@/i18n';
import { AuthStatusCard } from '@/components/AuthStatusCard';
import { GoogleSignInButton } from '@/components/GoogleSignInButton';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';
import { getAssetImageSource } from '@/services/assetImageSource';
import appColors from '@/theme/colors';

export default function LoginScreen() {
  const { user, setUser } = useUser();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const { debug: debugParam, reason, returnTo } = useLocalSearchParams<{ debug?: string; reason?: string; returnTo?: string }>();
  const { t } = useI18n();

  const [error, setError] = useState<string | null>(null);
  const debug = useMemo(() => debugParam === 'true', [debugParam]);
  const loginHeroImage = useMemo(() => getAssetImageSource('images/login-hero.png', 'hero', '/login'), []);
  const { loading, handleRedirect } = useAuthRedirect({
    user,
    setUser,
    onError: setError,
    debug,
    returnTo: typeof returnTo === 'string' ? returnTo : null,
  });

  useEffect(() => {
    if (!isNavigationReady) return;
    if (user) {
      // handled by Redirect below
      return;
    }
    if (reason === 'user-not-found') {
      setError(t('login.userNotFound'));
    }
  }, [user, isNavigationReady, reason, t]);

  return (
    <>
      {isNavigationReady && user ? <Redirect href={`/welcome${debug ? '?debug=true' : ''}`} /> : null}
      <View style={styles.container}>
        <Image source={loginHeroImage} style={styles.hero} resizeMode="contain" />
        <Text style={styles.title}>{t('auth.loginTitle')}</Text>
        <LanguageSwitcher />
        <AuthStatusCard
          title={t('auth.loginCardTitle')}
          error={error}
          buttonLabel={loading ? t('auth.loggingIn') : t('auth.loginCta')}
          buttonTestID="login-redirect-button"
          onPress={handleRedirect}
          disabled={loading}
        >
          <GoogleSignInButton
            label={t('auth.continueWithGoogle')}
            onPress={() => handleRedirect('google')}
            disabled={loading}
          />
          <View style={styles.dividerRow}>
            <View style={styles.dividerLine} />
            <Text style={styles.dividerText}>{t('auth.orDivider')}</Text>
            <View style={styles.dividerLine} />
          </View>
          {debug ? <Text style={styles.label}>{t('auth.debugLabel')}</Text> : null}
          {debug ? (
            <Text style={styles.value}>
              {user ? `${user.forename} (${user.id})` : t('auth.debugNone')}
            </Text>
          ) : null}
          <Text style={styles.label}>{t('auth.loginSubtitle')}</Text>
          <Text style={styles.label}>{t('auth.loginHint')}</Text>
        </AuthStatusCard>
      </View>
    </>
  );
}

const cardShadow = Platform.select({
  web: { boxShadow: '0px 4px 10px rgba(0,0,0,0.25)' },
  default: {
    shadowColor: appColors.text.secondary,
    shadowOpacity: 0.25,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
  },
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 24,
    gap: 16,
    justifyContent: 'center',
    backgroundColor: appColors.background.app,
  },
  hero: {
    width: 496,
    height: 196,
    alignSelf: 'center',
    marginBottom: 8,
  },
  title: {
    fontSize: 26,
    fontWeight: 'bold',
    textAlign: 'center',
    color: appColors.text.primary,
  },
  label: {
    fontSize: 14,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  value: {
    fontSize: 16,
    fontWeight: '600',
    color: appColors.text.primary,
    marginBottom: 4,
  },
  dividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 10,
    marginVertical: 2,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: appColors.border.strong,
  },
  dividerText: {
    color: appColors.text.secondary,
    fontSize: 13,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
});
